import * as React from 'react';
import useRestricted from '../hooks/useRestricted';
import { observer } from 'mobx-react-lite';
import { ControlPoint, Plan } from '../services/PlansService';
import { AppContext } from '../contexts/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import NewPlanDivideOwn from '../components/plans/NewPlanDivideOwn';
import Snackbar from '@mui/material/Snackbar';

export type NewPlanState = "type" | "race" | "add_race_confirmation" | "own" | "divide" | "finish";

const EditPlanCPs: React.FC = observer(() => {

  useRestricted();

  const { userService, plansService } = React.useContext(AppContext);

  const navigate = useNavigate();

  const [plan, setPlan] = React.useState<Partial<Plan>>({});
  const [isSavingPlan, setIsSavingPlan] = React.useState<NewPlanState>();
  const [error, setError] = React.useState(false);
  const [gpxFile, setGpxFile] = React.useState<File>();
  const [initialControlPoints, setInitialControlPoints] = React.useState<ControlPoint[]>([]);

  const { userId, planId } = useParams();

  React.useEffect(() => {
    if (!userService.user?.id || !planId || !userId || (userService.user?.id !== userId)) {
      if (!userService.isLoadingUser) {
        navigate("/plans");
      }
      return;
    }

    if (plan && plan.id === planId) {
      return;
    }

    const loadPlan = async () => {
      try {
        const loadedPlan = await plansService.loadPlan(userId, planId);

        if (!loadedPlan) {
          navigate("/plans");
          return;
        }

        const gpxSuccess = await plansService.loadGpxFile(`usersFiles/${loadedPlan.userId}`, userId, planId);
        if (!gpxSuccess) {
          setError(true);
          navigate("/plans");
          return;
        }

        setPlan({ ...loadedPlan });
        setGpxFile(gpxSuccess);

        const initialControlPoints = loadedPlan.controlPoints || [];
        if (initialControlPoints.length >= 2) {
          initialControlPoints.shift();
          initialControlPoints.pop();
        }
        setInitialControlPoints(initialControlPoints);
      } catch (error) {
        console.error("Error loading plan", error);
        navigate("/plans");
      }
    };

    loadPlan();
  }, [userService.isLoadingUser, plansService, planId, userId, userService.user?.id, navigate, plan]);


  const handleStateChange = async (newState: NewPlanState) => {
    if (!userService.user?.id) {
      return;
    }

    if (newState === "finish") {

      setIsSavingPlan(newState);
      try {
        const updatedPlan = { ...plan };
        const numberOfSections = (updatedPlan.sections?.length) || 0;
        if (updatedPlan.plannedEffort && updatedPlan.plannedEffort.length > numberOfSections) {
          updatedPlan.plannedEffort = updatedPlan.plannedEffort.slice(0, numberOfSections);
        }
        if (updatedPlan.plannedNutrition && updatedPlan.plannedNutrition.length > numberOfSections + (plan.controlPoints?.length || 0)) {
          updatedPlan.plannedNutrition = updatedPlan.plannedNutrition.slice(0, numberOfSections + (plan.controlPoints?.length || 0));
        }
        setPlan(updatedPlan);
        await plansService.upsertPlan(userService.user.id, updatedPlan as Plan);
        setIsSavingPlan(undefined);
        plansService.clearPlan();
        navigate(`/plans/${userService.user.id}/${plan.id}`, { replace: true });
      } catch (error) {
        console.error(error);
        setError(true);
        setIsSavingPlan(undefined);
      }

    } else {
      plansService.clearPlan();
      navigate(`/plans/${userService.user.id}/${plan.id}`, { replace: true });
    }

  }

  const setPlanData = (newPlanData: Partial<Plan>) => {
    setPlan({ ...plan, ...newPlanData });
  }


  return <>
    <Snackbar open={error} autoHideDuration={5000} onClose={() => setError(false)} message="There was an error saving the plan. Please try again" />
    <NewPlanDivideOwn onStateChange={handleStateChange} setPlanData={setPlanData} plan={plan} userGpxFile={gpxFile} loading={isSavingPlan} initialControlPoints={initialControlPoints} />
  </>
});

export default EditPlanCPs;
